import React, { useEffect, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

/*
  PaymentsList
  Props:
    - userId: string (optional)
    - carId: string (optional)
*/
export default function PaymentsList({ userId, carId }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [txValues, setTxValues] = useState({});
  const [txLoading, setTxLoading] = useState({});

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString();
    } catch {
      return String(d);
    }
  };

  const handleTxChange = (id, value) => {
    setTxValues((s) => ({ ...(s || {}), [id]: value }));
  };

  const submitTransactionId = async (payment) => {
    const id = payment._id || payment.id;
    const value = (txValues[id] || "").trim();
    if (!id || !value) return;
    setTxLoading((s) => ({ ...(s || {}), [id]: true }));
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/subscriptions/update/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify({ transactionId: value }),
        }
      );
      if (!res.ok) throw new Error("Failed to save transaction id");
      const updated = await res.json();
      setPayments((list) => list.map((p) => (p._id === updated._id ? updated : p)));
      setTxValues((s) => ({ ...(s || {}), [id]: "" }));
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save transaction id");
    } finally {
      setTxLoading((s) => ({ ...(s || {}), [id]: false }));
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = JSON.parse(localStorage.getItem("auth_token_crm"))?.token;
      const q = new URLSearchParams();
      // request subscriptions list and extract payment-related fields
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URI}/subscriptions/list?${q.toString()}&limit=7&carId=${carId}&userId=${userId}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch payments");
      const result = await res.json();
      console.log("Fetched payments:", result);
      const raw = result?.data ?? result;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      setPayments(list);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Error");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId && !carId) return;
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, carId]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        width: "100%",
        maxWidth: { xs: "100%", sm: "50%", md: "31%" },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle2">Recent Payments</Typography>
        {loading && <CircularProgress size={14} />}
      </Box>

      {error ? (
        <Typography color="error" variant="caption">{error}</Typography>
      ) : payments.length === 0 && !loading ? (
        <Typography variant="caption">No payments found.</Typography>
      ) : (
        <Table size="small" sx={{ width: "100%", minWidth: 320 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ p: "6px 8px" }}><Typography variant="caption">From</Typography></TableCell>
              <TableCell sx={{ p: "6px 8px" }}><Typography variant="caption">To</Typography></TableCell>
              <TableCell sx={{ p: "6px 8px" }} align="right"><Typography variant="caption">Price</Typography></TableCell>
              <TableCell sx={{ p: "6px 8px", maxWidth: 160 }}><Typography variant="caption">Transaction ID</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((p, i) => (
              <TableRow key={p._id || i}>
                <TableCell sx={{ p: "6px 8px" }}>
                  <Typography variant="caption">{formatDate(p.subscriptionStartDate || p.startDate || p.from)}</Typography>
                </TableCell>
                <TableCell sx={{ p: "6px 8px" }}>
                  <Typography variant="caption">{formatDate(p.subscriptionExpireDate || p.expireDate || p.to)}</Typography>
                </TableCell>
                <TableCell sx={{ p: "6px 8px" }} align="right">
                  <Typography variant="caption">{p.price ?? "-"}</Typography>
                </TableCell>
                <TableCell sx={{ p: "6px 8px", maxWidth: 220 }}>
                  {p.transactionId ? (
                    <Typography variant="caption" sx={{ display: "block", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.transactionId}
                    </Typography>
                  ) : (
                    <TextField
                      size="small"
                      placeholder="txn id"
                      value={txValues[p._id || p.id] || ""}
                      onChange={(e) => handleTxChange(p._id || p.id, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") submitTransactionId(p); }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => submitTransactionId(p)}
                              disabled={!((txValues[p._id || p.id] || "").trim()) || txLoading[p._id || p.id]}
                            >
                              {txLoading[p._id || p.id] ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ maxWidth: 200 }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
